import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AddressView } from '@capsico/contracts';
import { deleteAddress, fetchAddresses, setDefaultAddress } from '../api/customer';
import { errorMessage, errorRef } from '../api/client';
import { setDeliveryPoint, useDeliveryPoint } from './useDeliveryPoint';
import { showToast } from './useToast';

export function addressTitle(
  address: AddressView,
  labels: Record<'home' | 'work' | 'other', string>,
): string {
  return address.label === 'other' ? (address.labelText ?? labels.other) : labels[address.label];
}

export function addressLine(address: AddressView): string {
  return [address.house, address.floor, address.area].filter(Boolean).join(', ');
}

/** Saved addresses with optimistic delete / make-default (rolled back if the server refuses). */
export function useAddresses(enabled: boolean, messages: { deleted: string }) {
  const client = useQueryClient();
  const point = useDeliveryPoint();
  const query = useQuery({
    queryKey: ['addresses'],
    queryFn: fetchAddresses,
    enabled,
    staleTime: 60_000,
  });

  const remove = useMutation({
    mutationFn: deleteAddress,
    onMutate: async (id: string) => {
      await client.cancelQueries({ queryKey: ['addresses'] });
      const previous = client.getQueryData<AddressView[]>(['addresses']);
      client.setQueryData<AddressView[]>(['addresses'], (rows) =>
        (rows ?? []).filter((row) => row.id !== id),
      );
      return { previous };
    },
    onError: (failure, _id, context) => {
      if (context?.previous) client.setQueryData(['addresses'], context.previous);
      showToast(errorMessage(failure), { tone: 'negative', ref: errorRef(failure) });
    },
    onSuccess: (_result, id) => {
      if (point?.addressId === id) setDeliveryPoint(null);
      showToast(messages.deleted);
    },
    onSettled: () => void client.invalidateQueries({ queryKey: ['addresses'] }),
  });

  const makeDefault = useMutation({
    mutationFn: setDefaultAddress,
    onMutate: async (id: string) => {
      await client.cancelQueries({ queryKey: ['addresses'] });
      const previous = client.getQueryData<AddressView[]>(['addresses']);
      client.setQueryData<AddressView[]>(['addresses'], (rows) =>
        (rows ?? []).map((row) => ({ ...row, isDefault: row.id === id })),
      );
      return { previous };
    },
    onError: (failure, _id, context) => {
      if (context?.previous) client.setQueryData(['addresses'], context.previous);
      showToast(errorMessage(failure), { tone: 'negative', ref: errorRef(failure) });
    },
    onSuccess: (rows) => client.setQueryData(['addresses'], rows),
  });

  return {
    loading: query.isLoading,
    failed: query.isError,
    retry: () => void query.refetch(),
    addresses: query.data ?? [],
    remove: (id: string) => remove.mutate(id),
    makeDefault: (id: string) => makeDefault.mutate(id),
  };
}
